import { createActionController } from "./actions.js";
import { DEFAULT_CONFIG } from "./defaults.js";
import { EmojiHider } from "./emoji.js";
import { createStorage } from "./storage.js";
import { applyThemeVariables, BASE_STYLES } from "./styles.js";
import { createUI } from "./ui.js";
import { deepMerge, hotkeyMatches, parseHotkey, toCamelCase } from "./utils.js";

const STYLE_ID = "readlygo-base-style";

let runtime = null;
let pendingConfig = null;

function init(userConfig = {}) {
  if (!document.body) {
    pendingConfig = userConfig;
    document.addEventListener("DOMContentLoaded", handleDomReady, { once: true });
    return ReadlyGo;
  }

  if (runtime) {
    destroy();
  }

  const config = normalizeConfig(deepMerge(DEFAULT_CONFIG, userConfig || {}));
  ensureStyles(config.styleNonce);
  applyThemeVariables(config.theme);

  const storage = createStorage(config.storageKey);
  const languageStorage = createStorage(`${config.storageKey}:language`);
  const emoji = new EmojiHider();
  const ui = createUI(config, {
    onToggle: (action) => {
      controller.dispatch(action);
    },
    onSpinner: (action, delta) => {
      const current = controller.getState().values[action] ?? 0;
      controller.dispatch(action, current + delta);
    },
    onReset: () => {
      reset();
    },
    onLanguageChange: (language) => {
      if (config.rememberLanguage) {
        languageStorage.save(language);
      }
      window.dispatchEvent(
        new CustomEvent("ReadlyGoLanguageChanged", {
          detail: { language },
        }),
      );
    },
  });

  const controller = createActionController(config, {
    ui,
    storage,
    emoji,
  });

  if (config.remember) {
    controller.load(storage.load());
  }
  const rememberedLanguage = config.rememberLanguage ? languageStorage.load() : null;
  const configuredDefaultLanguage =
    config.defaultLanguage || config.language || config.localization?.defaultLanguage || "en";

  ui.mount();
  ui.setLanguage(
    typeof rememberedLanguage === "string" ? rememberedLanguage : configuredDefaultLanguage,
    { persist: false },
  );
  controller.applyAll();

  const openHotkey = parseHotkey(config.openHotkey);
  const onOpenHotkey = (event) => {
    const target = event.target;
    if (
      target &&
      (target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT" ||
        target.isContentEditable)
    ) {
      return;
    }

    if (hotkeyMatches(event, openHotkey)) {
      event.preventDefault();
      ui.toggleOpen();
      return;
    }
    if (event.key === "Escape" && ui.isOpen()) {
      event.preventDefault();
      ui.close();
    }
  };
  document.addEventListener("keydown", onOpenHotkey);

  const onHeadless = (event) => {
    const detail = event.detail;
    if (!detail || !detail.action) {
      return;
    }
    controller.dispatch(detail.action, detail.value);
  };
  window.addEventListener("readabler-headless", onHeadless);

  if (config.startOpen) {
    ui.open();
  }

  runtime = {
    config,
    storage,
    languageStorage,
    controller,
    ui,
    emoji,
    listeners: {
      onOpenHotkey,
      onHeadless,
    },
  };

  return ReadlyGo;
}

function dispatch(actionOrPayload, value) {
  if (!runtime) {
    return;
  }
  if (typeof actionOrPayload === "string") {
    runtime.controller.dispatch(actionOrPayload, value);
    return;
  }
  if (!actionOrPayload || typeof actionOrPayload !== "object") {
    return;
  }
  runtime.controller.dispatch(actionOrPayload.action, actionOrPayload.value);
}

function reset() {
  if (!runtime) {
    return;
  }
  runtime.controller.reset();
  if (runtime.config.remember) {
    runtime.storage.save(runtime.controller.getState());
  } else {
    runtime.storage.reset();
  }
}

function destroy() {
  if (!runtime) {
    return;
  }
  document.removeEventListener("keydown", runtime.listeners.onOpenHotkey);
  window.removeEventListener("readabler-headless", runtime.listeners.onHeadless);

  runtime.controller.destroy();
  runtime.ui.destroy();
  runtime.emoji.destroy();

  runtime = null;
}

function handleDomReady() {
  if (pendingConfig) {
    const config = pendingConfig;
    pendingConfig = null;
    init(config);
  }
}

function ensureStyles(styleNonce = "") {
  if (document.getElementById(STYLE_ID)) {
    return;
  }
  const style = document.createElement("style");
  style.id = STYLE_ID;
  if (styleNonce) {
    style.setAttribute("nonce", styleNonce);
  }
  style.textContent = BASE_STYLES;
  document.head.appendChild(style);
}

function normalizeConfig(config) {
  const normalized = config;
  const configuredDefaultLanguage = normalizeLanguageCode(
    normalized.defaultLanguage || normalized.language || normalized.localization?.defaultLanguage,
  );
  if (configuredDefaultLanguage) {
    normalized.defaultLanguage = configuredDefaultLanguage;
    normalized.language = configuredDefaultLanguage;
    normalized.localization = normalized.localization || {};
    normalized.localization.defaultLanguage =
      normalizeLanguageCode(normalized.localization.defaultLanguage) || configuredDefaultLanguage;
  }

  if (
    normalized.buttonMarginX !== undefined &&
    normalized.buttonMarginX !== null &&
    (normalized.buttonOffsetX === undefined || normalized.buttonOffsetX === null)
  ) {
    normalized.buttonOffsetX = normalized.buttonMarginX;
  }
  if (
    normalized.buttonMarginY !== undefined &&
    normalized.buttonMarginY !== null &&
    (normalized.buttonOffsetY === undefined || normalized.buttonOffsetY === null)
  ) {
    normalized.buttonOffsetY = normalized.buttonMarginY;
  }

  const floating = normalized.floatingButton;
  if (!floating || typeof floating !== "object") {
    return normalized;
  }

  if (typeof floating.enabled === "boolean") {
    normalized.showButton = floating.enabled;
  }
  if (floating.position) {
    normalized.buttonPosition = floating.position;
  }
  if (floating.variant) {
    normalized.buttonVariant = floating.variant;
  }
  if (floating.iconUrl) {
    normalized.buttonIconUrl = floating.iconUrl;
  }
  if (floating.iconSize !== undefined && floating.iconSize !== null) {
    normalized.buttonIconSize = floating.iconSize;
  }
  if (floating.label) {
    normalized.buttonLabel = floating.label;
  }
  if (floating.ariaLabel) {
    normalized.buttonAriaLabel = floating.ariaLabel;
  }
  if (floating.title) {
    normalized.buttonTitle = floating.title;
  }
  if (floating.background) {
    normalized.buttonBackground = floating.background;
  }
  if (floating.textColor) {
    normalized.buttonTextColor = floating.textColor;
  }
  if (floating.iconColor) {
    normalized.buttonIconColor = floating.iconColor;
  }
  if (floating.borderColor) {
    normalized.buttonBorderColor = floating.borderColor;
  }
  if (floating.boxShadow !== undefined && floating.boxShadow !== null) {
    normalized.buttonBoxShadow = floating.boxShadow;
  }
  if (floating.boxShadowHover !== undefined && floating.boxShadowHover !== null) {
    normalized.buttonBoxShadowHover = floating.boxShadowHover;
  }
  if (floating.hoverBoxShadow !== undefined && floating.hoverBoxShadow !== null) {
    normalized.buttonBoxShadowHover = floating.hoverBoxShadow;
  }
  if (floating.padding !== undefined && floating.padding !== null) {
    normalized.buttonPadding = floating.padding;
  }
  if (floating.margin !== undefined && floating.margin !== null) {
    normalized.buttonMargin = floating.margin;
    if (normalized.buttonOffsetX === undefined || normalized.buttonOffsetX === null) {
      normalized.buttonOffsetX = floating.margin;
    }
    if (normalized.buttonOffsetY === undefined || normalized.buttonOffsetY === null) {
      normalized.buttonOffsetY = floating.margin;
    }
  }
  if (
    floating.marginX !== undefined &&
    floating.marginX !== null &&
    (normalized.buttonOffsetX === undefined || normalized.buttonOffsetX === null)
  ) {
    normalized.buttonOffsetX = floating.marginX;
  }
  if (
    floating.marginY !== undefined &&
    floating.marginY !== null &&
    (normalized.buttonOffsetY === undefined || normalized.buttonOffsetY === null)
  ) {
    normalized.buttonOffsetY = floating.marginY;
  }
  if (floating.offsetX !== undefined && floating.offsetX !== null) {
    normalized.buttonOffsetX = floating.offsetX;
  }
  if (floating.offsetY !== undefined && floating.offsetY !== null) {
    normalized.buttonOffsetY = floating.offsetY;
  }

  return normalized;
}

function getState() {
  if (!runtime) {
    return null;
  }
  const state = runtime.controller.getState();
  state.language = runtime.ui.getLanguage?.() || runtime.config.language || "en";
  return state;
}

function normalizeLanguageCode(value) {
  if (typeof value !== "string") {
    return "";
  }
  return value.trim().toLowerCase();
}

function isReady() {
  return Boolean(runtime);
}

function normalizeActionName(action) {
  return toCamelCase(action);
}

const ReadlyGo = {
  init,
  dispatch,
  reset,
  destroy,
  getState,
  isReady,
  normalizeActionName,
};

if (typeof window !== "undefined" && !window.ReadablerLite) {
  // Backward-compatible global alias.
  window.ReadablerLite = ReadlyGo;
}

export {
  init,
  dispatch,
  reset,
  destroy,
  getState,
  isReady,
  normalizeActionName,
};

export default ReadlyGo;
